import {
  getCategoryTagNameFromId,
  getCategoryTagNameFromIdSSR,
} from '../utils/getCategoryTagNameFromId';
import { isWebsiteInternallyRouted } from 'src/utils/isWebsiteInternallyRouted';
import { IS_CLIENT, IS_SERVER } from './checkRenderEnv';
import { getBasicWebsiteInfoByCTX } from 'src/pageUtils/commonUtils/ctxUtils';

function getCidFromRouteName(routeName) {
  const cidParam = '?cid=';
  try {
    const cid = routeName.split(cidParam)[1];
    return parseInt(cid);
  } catch (err) {
    return -1;
  }
}

/**
 * This method should be used to get redirection links on both server & client,
 * This handles added prefixes, for internally routed domains using (showroom.xx.xx), purchased domains (xx.in) & for preview paths too.
 * Caveat: => This also handle category/collections redirections, so, if those redirection link are to be fetched on server side, then they
 * need to be present in server store.
 */
export const getRoute = (routeName = '', domain = '', ctx = null) => {
  try {
    const isBuilderPreview = !IS_SERVER
      ? location.href.includes(PATH.BUILDER_PREVIEW)
      : getBasicWebsiteInfoByCTX(ctx).isPreviewRoute;
    if (
      !IS_SERVER &&
      location.href.includes(PATH.DASHY_THEME_PREVIEW) &&
      !isBuilderPreview
    ) {
      return location.pathname;
    }
    if (
      (routeName.includes(PATH.SHOP) || routeName.includes(PATH.PRODUCT)) &&
      routeName.includes('?cid=')
    ) {
      const cid = getCidFromRouteName(routeName);
      const categoryName = encodeURIComponent(
        (IS_SERVER
          ? // This will work only when SSR store is hydrated else it will throw 404, it is hydrated using updateSSRStore method
            getCategoryTagNameFromIdSSR(cid, domain)
          : getCategoryTagNameFromId(cid)
        )
          .replace(/ /g, '')
          .replace(/\//g, '-')
      );
      routeName = `shop/${categoryName}?cid=${cid}`;
    }

    let domainPrefix = '/';
    const pathname = ctx ? ctx?.resolvedUrl : IS_CLIENT ? location.pathname : '';
    if (domain) {
      if (pathname?.startsWith?.(`/${PATH.BUILDER_PREVIEW}`)) {
        domainPrefix = `/${PATH.BUILDER_PREVIEW}/${domain}/`;
      } else {
        if (domain.startsWith('d-')) {
          if (domain.startsWith('d-temporary-')) {
            const domainParts = domain.split('-');
            domainPrefix = `/${domainParts[1]}-${domainParts[2]}/`;
          } else domainPrefix = `/${domain.split('-')[1]}/`;
        } else if (checkIsValidDomain(domain)) {
          if (pathname?.startsWith?.(`/${domain}`)) {
            // domain is in pathname
            domainPrefix = `/${domain}/`;
          }
        } else {
          domainPrefix = `/${domain}/`;
        }
      }
    }
    if (pathname?.startsWith?.(`/${PATH.BUILDER_PREVIEW}`)) {
      const cleanDomain = domain.startsWith('d-') ? domain.slice(2) : domain;
      domainPrefix = `/${PATH.BUILDER_PREVIEW}/${cleanDomain}/`;
    }
    if (routeName?.startsWith('/') && domainPrefix?.endsWith('/')) {
      routeName = routeName?.slice(1);
    }

    return `${domainPrefix}${routeName}`;
  } catch (error) {
    console.log(error);
    return '/404';
  }
};

export const checkIsValidDomain = (domain) => {
  const re = new RegExp(
    /^((?:(?:(?:\w[\.\-\+]?)*)\w)+)((?:(?:(?:\w[\.\-\+]?){0,62})\w)+)\.(\w{2,6})$/
  );
  return domain.match(re);
};

export const getDefaultCanonicalUrl = (url) => {
  const urlObj = url?.split('?');
  // we just need to fetch the url part before query param on pdp page.
  if (urlObj?.length === 2 && url?.includes(PATH.PDP)) {
    return urlObj[0];
  }
  return url;
};

export const getSeoProductRoute = (city, domain) => {
  if (domain.startsWith('d-temporary-'))
    return `/${PATH.productCity(city)}/${domain.split('-')[1]}-${domain.split('-')[2]}`;
  else if (domain.startsWith('d-'))
    return `/${PATH.productCity(city)}/${domain.split('-')[1]}`;
  else {
    return `/${PATH.productCity(city)}/${domain}`;
  }
};

export const PATH = {
  /** APP PATHS */
  PRODUCT: '',
  SHOP: 'shop',
  PDP: '/product/',
  VERIFY_OTP: 'verify-otp',
  MEMBER_LOGIN: 'member-login',
  ORDERS: 'orders',
  STORE_CLOSE: 'store-close',
  CART: 'cart',
  SEARCH: 'search',
  _404: '404',
  paymentProcess: (txnID) => `payment-process/${txnID}`,
  paymentProcessing: (txnID, paymentMethod, orderHash) =>
    `payment/processing/${txnID}?method=${paymentMethod}${
      orderHash ? `&oHash=${orderHash}` : ''
    }`,
  DELIVERY_DETAILS: 'delivery-details',
  DELIVERY_LIST: 'delivery-list',
  ADD_DELIVERY: 'add-delivery',
  orderStatus: (hash) => `order-status/${hash}`,
  orderReceipt: (hash) => `order-receipt/${hash}`,
  thankyou: (hash) => `thankyou/${hash}`,
  orderConfirm: (hash) => `order-confirm/${hash}`,
  payment: (hash) => `payment/${hash}`,
  cardPayment: (hash) => `payment/card/${hash}`,
  paymentFailed: (hash) => `payment-failed/${hash}`,
  productDescription: (pId, pname) => `product/${pId}/${pname}`,
  ABOUT_US: `about-us`,
  STORE_POLICIES: `policy`,
  DOMAIN_HOME: `/`,
  additionalPage: (route) => `/${route}`,
  USER_COUPONS_LIST: 'offers-list',
  CATEGORIES: 'categories',
  emiOptions: (emiPrice) => `emi-options?emiPrice=${emiPrice}`,
  EMI_PAYMENT: `emi-payment`,
  collectionCategories: (collectionId) => `collections/${collectionId}`,
  WEBSITE_BUILDER: '/widgets/',
  STORE: '/store/',
  MY_ACCOUNT: 'my-account', // only for app when user is logged in
  BUILDER_PREVIEW: 'play-preview',
  BLOG: '/blog',
  DASHY_THEME_PREVIEW: 'preview',
  WISHLIST: '/wishlist',
  COLLECTIONS: 'collections',
};

/**
 * Server side 3XX redirection
 * @param {import('next').NextPageContext} ctx
 * @param {String} redirectUrl Url to redirect to
 * @param {Number} status @default 301 Can be 301, 302, 303
 * @returns
 */
export const redirectToUrl = (ctx, redirectUrl, status = 301) => {
  if (ctx && redirectUrl) {
    ctx.res.writeHead(status, { location: redirectUrl });
    ctx.res.end();
    return { props: { status } };
  } else {
    return null;
  }
};

/**
 * Server side 404 redirection
 * @param {import('next').NextPageContext} ctx
 * @returns
 */
export const redirectTo404 = (ctx) => {
  ctx.res.writeHead(302, { location: '/404' });
  ctx.res.end();
};

/**
 * Server side 3XX redirection for store if redirect url exists
 * @param {import('next').NextPageContext} ctx
 *
 */
export const redirectStoreIfNeeded = (ctx, storeInfo) => {
  if (ctx) {
    if (storeInfo?.redirect_url) {
      redirectToUrl(ctx, storeInfo?.redirect_url);
      return true;
    }
    if (!storeInfo?.status) {
      redirectTo404(ctx);
      return true;
    }
  }
  return false;
};

/**
 * Server side home redirection, when purchased domain has missing complete subscription
 * @param {import('next').NextPageContext} ctx
 * @returns
 */
export const isRouteHomepage = (ctx) => {
  const isDomainInternallyRouted = isWebsiteInternallyRouted(
    ctx.req.headers?.domain || ctx.req.headers.host
  );
  const path = isDomainInternallyRouted
    ? ctx.req.url?.replace(`/${ctx.params?.domain}`, PATH.DOMAIN_HOME)
    : ctx.req.url;

  if (
    path === PATH.DOMAIN_HOME ||
    [PATH.WEBSITE_BUILDER, PATH.BUILDER_PREVIEW].includes(ctx.req.url)
  ) {
    return false;
  }
  return true;
};
